import React, { useState, useMemo } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, parseISO, isSameDay } from 'date-fns';
import { MdClose, MdCheckCircle, MdRadioButtonUnchecked, MdEdit, MdDelete } from 'react-icons/md';
import { getIconComponent } from '../utils/iconHelper';
import './MonthlyTransactionCalendar.css';

function MonthlyTransactionCalendar({ 
  transactions, 
  currentDate,
  categories, 
  accounts, 
  getCategory, 
  getAccount, 
  formatCurrency,
  onEdit,
  onDelete,
  onTogglePaid,
  onEffectuateVirtual,
  onInactivateVirtual
}) {
  const [selectedDay, setSelectedDay] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  // Calcular dias do mês
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Calcular o primeiro dia da semana do mês (0 = domingo, 1 = segunda, etc.)
  const firstDayOfWeek = getDay(monthStart);

  // Agrupar transações por data
  const transactionsByDate = useMemo(() => {
    const grouped = {};
    
    transactions.forEach(transaction => {
      try {
        // Normalizar a data
        let dateKey = transaction.date;
        if (dateKey.includes('T')) {
          dateKey = dateKey.split('T')[0];
        }
        
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(transaction);
      } catch (error) {
        console.error('Erro ao processar data da transação:', transaction, error);
      }
    });
    
    return grouped;
  }, [transactions]);

  // Calcular totais por dia
  const getDayTotals = (dateKey) => {
    const dayTransactions = transactionsByDate[dateKey] || [];
    
    const paidIncome = dayTransactions
      .filter(t => t.type === 'income' && t.is_paid)
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);
    
    const paidExpense = dayTransactions
      .filter(t => t.type === 'expense' && t.is_paid)
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);
    
    const pendingIncome = dayTransactions
      .filter(t => t.type === 'income' && !t.is_paid)
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);
    
    const pendingExpense = dayTransactions
      .filter(t => t.type === 'expense' && !t.is_paid)
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);
    
    return {
      paidIncome,
      paidExpense,
      pendingIncome,
      pendingExpense,
      paidBalance: paidIncome - paidExpense,
      pendingBalance: pendingIncome - pendingExpense,
      count: dayTransactions.length,
      paidCount: dayTransactions.filter(t => t.is_paid).length,
      pendingCount: dayTransactions.filter(t => !t.is_paid).length
    };
  };

  // Abrir modal com detalhes do dia
  const handleDayClick = (day) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    const dayTransactions = transactionsByDate[dateKey];
    if (dayTransactions && dayTransactions.length > 0) {
      setSelectedDay({ date: day, dateKey });
      setShowModal(true);
    }
  };

  // Fechar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDay(null);
  };

  // Formatar data do modal
  const formatModalDate = (date) => {
    const weekDayNames = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
    const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    
    const weekDay = weekDayNames[date.getDay()];
    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    return `${weekDay}, ${day} de ${month} de ${year}`;
  };

  return (
    <div className="monthly-transaction-calendar">
      {/* Grid do calendário */}
      <div className="monthly-calendar-grid">
        {/* Cabeçalho dos dias da semana */}
        {weekDays.map(day => (
          <div key={day} className="monthly-calendar-weekday">
            {day}
          </div>
        ))}

        {/* Células vazias antes do primeiro dia do mês */}
        {Array.from({ length: firstDayOfWeek }).map((_, index) => (
          <div key={`empty-${index}`} className="monthly-calendar-day empty"></div>
        ))}

        {/* Dias do mês */}
        {daysInMonth.map(day => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dayTotals = getDayTotals(dateKey);
          const hasTransactions = dayTotals.count > 0;

          return (
            <div
              key={dateKey}
              className={`monthly-calendar-day ${hasTransactions ? 'has-transactions' : ''} ${dayTotals.pendingCount > 0 ? 'has-pending' : ''}`}
              onClick={() => hasTransactions && handleDayClick(day)}
            >
              <div className="monthly-day-number">{day.getDate()}</div>
              
              {hasTransactions && (
                <div className="monthly-day-transactions">
                  <div className="monthly-transaction-badges">
                    {dayTotals.paidCount > 0 && (
                      <div className="monthly-badge paid" title={`${dayTotals.paidCount} paga(s)`}>
                        {dayTotals.paidCount}
                      </div>
                    )}
                    {dayTotals.pendingCount > 0 && (
                      <div className="monthly-badge pending" title={`${dayTotals.pendingCount} pendente(s)`}>
                        {dayTotals.pendingCount}
                      </div>
                    )}
                  </div>
                  
                  <div className="monthly-day-totals">
                    {dayTotals.paidBalance !== 0 && (
                      <div className={`monthly-total-paid ${dayTotals.paidBalance >= 0 ? 'positive' : 'negative'}`}>
                        {formatCurrency(dayTotals.paidBalance)}
                      </div>
                    )}
                    {dayTotals.pendingBalance !== 0 && (
                      <div className="monthly-total-pending">
                        P: {formatCurrency(Math.abs(dayTotals.pendingBalance))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Modal com detalhes das transações do dia */}
      {showModal && selectedDay && (
        <div className="monthly-calendar-modal-overlay" onClick={handleCloseModal}>
          <div className="monthly-calendar-modal" onClick={(e) => e.stopPropagation()}>
            <div className="monthly-calendar-modal-header">
              <h3>{formatModalDate(selectedDay.date)}</h3>
              <button className="monthly-calendar-modal-close" onClick={handleCloseModal}>
                <MdClose size={24} />
              </button>
            </div>

            <div className="monthly-calendar-modal-body">
              {transactionsByDate[selectedDay.dateKey]?.map((transaction) => {
                const category = getCategory(transaction.category_id);
                const account = getAccount(transaction.account_id);
                const CategoryIcon = category ? getIconComponent(category.icon) : null;

                return (
                  <div
                    key={transaction.id}
                    className={`monthly-calendar-transaction-item ${transaction.type} ${!transaction.is_paid ? 'pending' : ''}`}
                  >
                    <div className="monthly-calendar-transaction-icon-wrapper">
                      <div
                        className="monthly-calendar-transaction-icon"
                        style={{ backgroundColor: category?.color || '#999' }}
                      >
                        {CategoryIcon && <CategoryIcon size={20} color="white" />}
                      </div>
                    </div>

                    <div className="monthly-calendar-transaction-content">
                      <div className="monthly-calendar-transaction-header">
                        <div className="monthly-transaction-title">
                          <h4 className="monthly-calendar-transaction-description">
                            {transaction.description}
                          </h4>
                          {transaction.is_virtual && (
                            <span className="monthly-transaction-badge virtual">VIRTUAL</span>
                          )}
                          {transaction.nature === 'recurring' && (
                            <span className="monthly-transaction-badge recurring">
                              🔄 {transaction.current_occurrence}/{transaction.occurrences}
                            </span>
                          )}
                        </div>
                        <span className={`monthly-calendar-transaction-amount ${transaction.type}`}>
                          {transaction.type === 'income' ? '+' : '-'}
                          {formatCurrency(Math.abs(transaction.amount))}
                        </span>
                      </div>

                      <div className="monthly-calendar-transaction-meta">
                        <div className="monthly-calendar-transaction-detail">
                          <span className="monthly-detail-label">Categoria:</span>
                          <span className="monthly-detail-value">{category?.name || '-'}</span>
                        </div>
                        <div className="monthly-calendar-transaction-detail">
                          <span className="monthly-detail-label">Conta:</span>
                          <span className="monthly-detail-value">{account?.name || '-'}</span>
                        </div>
                        <div className="monthly-calendar-transaction-detail">
                          <span className="monthly-detail-label">Status:</span>
                          <span className={`monthly-detail-value status ${transaction.is_paid ? 'paid' : 'pending'}`}>
                            {transaction.is_paid ? (
                              <>
                                <MdCheckCircle size={14} /> Pago
                              </>
                            ) : (
                              <>
                                <MdRadioButtonUnchecked size={14} /> Pendente
                              </>
                            )}
                          </span>
                        </div>
                      </div>

                      <div className="monthly-calendar-transaction-actions">
                        {transaction.is_virtual ? (
                          <>
                            <button
                              className="monthly-action-btn effectuate"
                              onClick={() => {
                                handleCloseModal();
                                onEffectuateVirtual(transaction);
                              }}
                              title="Efetivar Transação"
                            >
                              <MdCheckCircle size={18} />
                            </button>
                            <button
                              className="monthly-action-btn inactivate"
                              onClick={() => {
                                handleCloseModal();
                                onInactivateVirtual(transaction);
                              }}
                              title="Inativar Transação Fixa"
                            >
                              Inativar
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="monthly-action-btn toggle"
                              onClick={() => {
                                handleCloseModal();
                                onTogglePaid(transaction);
                              }}
                              title={transaction.is_paid ? 'Marcar como pendente' : 'Marcar como pago'}
                            >
                              {transaction.is_paid ? (
                                <MdRadioButtonUnchecked size={18} />
                              ) : (
                                <MdCheckCircle size={18} />
                              )}
                            </button>
                            <button
                              className="monthly-action-btn edit"
                              onClick={() => {
                                handleCloseModal();
                                onEdit(transaction);
                              }}
                              title="Editar"
                            >
                              <MdEdit size={18} />
                            </button>
                            <button
                              className="monthly-action-btn delete"
                              onClick={() => {
                                handleCloseModal();
                                onDelete(transaction.id, transaction.is_virtual);
                              }}
                              title="Excluir"
                            >
                              <MdDelete size={18} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="monthly-calendar-modal-footer">
              <div className="monthly-calendar-modal-summary">
                {(() => {
                  const totals = getDayTotals(selectedDay.dateKey);
                  return (
                    <>
                      {totals.paidBalance !== 0 && (
                        <div className="monthly-summary-item paid">
                          <span className="monthly-summary-label">Saldo Pago:</span>
                          <span className={`monthly-summary-value ${totals.paidBalance >= 0 ? 'positive' : 'negative'}`}>
                            {formatCurrency(totals.paidBalance)}
                          </span>
                        </div>
                      )}
                      {totals.pendingBalance !== 0 && (
                        <div className="monthly-summary-item pending">
                          <span className="monthly-summary-label">Saldo Pendente:</span>
                          <span className={`monthly-summary-value ${totals.pendingBalance >= 0 ? 'positive' : 'negative'}`}>
                            {formatCurrency(totals.pendingBalance)}
                          </span>
                        </div>
                      )}
                      <div className="monthly-summary-item total">
                        <span className="monthly-summary-label">Total do Dia:</span>
                        <span className={`monthly-summary-value ${(totals.paidBalance + totals.pendingBalance) >= 0 ? 'positive' : 'negative'}`}>
                          {formatCurrency(totals.paidBalance + totals.pendingBalance)}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MonthlyTransactionCalendar;
